import { useContext } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import type { ModuleKey } from '@erp/rpc';
import { AuthContext } from '@/context/auth.context';
import { useDefaultAgentModule } from '@/hooks/useAgentModule';
import { renderSharedToolResult } from '@/components/common/chat/useChatToolResultRenderer';
import { useNamiEnabled } from './useNamiEnabled';
import { useNavigablePages } from './useNavigablePages';

const HOME_MODULE: ModuleKey = 'home';

/**
 * The always-available Nami surface: when the current page registered no
 * agent module, the panel falls back to the page-less `home` module so free
 * chat, data Q&A and navigation work anywhere. Mirrors what `/chat` sends.
 */
export function useNamiDefaultSurface(): void {
  const gt = useGT();
  const enabled = useNamiEnabled();
  const { currentUser } = useContext(AuthContext);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pages = useNavigablePages();

  useDefaultAgentModule(
    {
      moduleKey: HOME_MODULE,
      currentPage: pathname,
      placeholder: gt('Ask the assistant anything…', {
        context: 'placeholder for the chat agent input',
      }),
      getModuleContext: () => ({
        userName: currentUser?.name,
        today: new Date().toISOString(),
        pages,
        // Tells the backend this panel renders the general toolkit, so page
        // modules layered over this surface get it too.
        generalToolkit: true,
      }),
      renderToolResult: (_message, toolResult) =>
        renderSharedToolResult(toolResult, pages),
    },
    { enabled },
  );
}
